import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { getCall } from './apicalls.service';
import {
    User,
    AppUser,
    UserAccess,
    UserPermission,
    Permission,
} from '@models/models';
import { environment } from '../../../environments/environment';
import { HeadersService } from './headers.service';

@Injectable({
    providedIn: 'root',
})
export class CredentialsApiService extends HeadersService {
    private currentUserSubject: BehaviorSubject<User>;
    public current_user: Observable<User>;
    public currentAppUserSubject: BehaviorSubject<AppUser>;
    public current_app_user: Observable<AppUser>;
    public currentUserPermissionSubject: BehaviorSubject<UserPermission>;
    public currentUserPermission: Observable<UserPermission>;
    private httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
        }),
    };
    public access_token: string = null;

    constructor(public _http: HttpClient, private router: Router) {
        super(_http);
        this.currentUserSubject = new BehaviorSubject<User>(
            JSON.parse(localStorage.getItem('current_user'))
        );
        this.current_user = this.currentUserSubject.asObservable();
        this.currentAppUserSubject = new BehaviorSubject<AppUser>(
            JSON.parse(localStorage.getItem('app_user'))
        );
        this.current_app_user = this.currentAppUserSubject.asObservable();
    }

    public get currentUserValue(): User {
        return this.currentUserSubject.value;
    }

    get appUser() {
        return this.currentAppUserSubject.asObservable();
    }

    public getHttpOptionsWithToken() {
        return {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
            }),
        };
    }

    /**
     * LOGIN
     * @param username
     * @param password
     */
    login(username: string, password: string) {
        const url = getCall('login', null);
        const httpData = new URLSearchParams();
        httpData.set('client_id', environment.client_id);
        httpData.set('client_secret', environment.client_secret);
        httpData.set('grant_type', 'password');
        httpData.set('username', username);
        httpData.set('password', password);

        return this._http
            .post<any>(url, httpData.toString(), this.httpOptions)
            .pipe(
                map((user) => {
                    // store user details
                    localStorage.setItem('current_user', JSON.stringify(user));
                    this.currentUserSubject.next(user);
                    localStorage.setItem('access_token', user.access_token);
                    this.access_token = user.access_token
                        ? user.access_token
                        : null;
                    return user;
                })
            );
    }

    getUser() {
        const params = { access: localStorage.getItem('user-access') };
        const url = getCall('user', null, params);
        return this._http.get(url, this.getHttpOptionsWithToken()).pipe(
            map((user) => {
                /* Necessary to avoid ObjectUnsubscribedError when logout and login */
                this.currentAppUserSubject = new BehaviorSubject<AppUser>(
                    JSON.parse(localStorage.getItem('app_user'))
                );
                this.current_app_user =
                    this.currentAppUserSubject.asObservable();

                localStorage.setItem('app_user', JSON.stringify(user));
                this.currentAppUserSubject.next(user);
                return user;
            })
        );
    }

    public get currentAppUserValue(): AppUser {
        return this.currentAppUserSubject.value;
    }

    getUserAccess() {
        const url = getCall('user-access', null);
        return this._http.get<Array<UserAccess>>(
            url,
            this.getHttpOptionsWithToken()
        );
    }

    updateUser(id: number, data: any) {
        const url = getCall('user', id);
        return this._http.patch(url, data, this.getHeaderWOCT()).pipe(
            map((user) => {
                localStorage.setItem('app_user', JSON.stringify(user));
                this.currentAppUserSubject.next(user);
                return user;
            })
        );
    }

    activateUser(uidb: string, token: string) {
        let url = `${getCall('activate-user')}${uidb}/${token}/`;

        return this._http.get(url, this.httpOptions);
    }

    /**
     * LOGOUT
     */
    logout() {
        // remove user from local storage and set current user to null
        localStorage.removeItem('current_user');
        localStorage.removeItem('app_user');
        localStorage.removeItem('user_type');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user-access');

        // this.currentUserSubject.next(null);
        // this.currentAppUserSubject.next(null);
        this.router.navigate(['/']);
    }

    /**
     * REFRESH TOKEN
     */
    refreshToken() {
        const url = getCall('login', null);
        const httpData = new URLSearchParams();
        httpData.set('client_id', environment.client_id);
        httpData.set('client_secret', environment.client_secret);
        httpData.set('grant_type', 'refresh_token');
        httpData.set('refresh_token', this.currentUserValue['refresh_token']);

        return this._http
            .post<any>(url, httpData.toString(), this.httpOptions)
            .pipe(
                map((user) => {
                    localStorage.setItem('current_user', JSON.stringify(user));
                    this.currentUserSubject.next(user);
                    localStorage.setItem('access_token', user.access_token);
                    this.access_token = user.access_token
                        ? user.access_token
                        : null;
                    return user;
                })
            );
    }
}
